<script setup>
import { AnnStore } from '@/front/stores/ann_store.js'
import { PlayerStore } from '@/front/stores/player_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { DesignStore } from '@/front/stores/design_store.js'
import { finishMessage } from '@/front/composables/ClickComposer.js'
import { PositionCalculator } from '@/front/composables/PositionCalculator.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
const videoStore = VideoStore()
const { getRelativeLayerDimensions } = PositionCalculator()
const playerStore = PlayerStore()
const clickStore = ClickStore()
const designStore = DesignStore()
import { localize } from '@/front/composables/StringComposer'
import { getBaseButtonStyle } from "@/front/composables/StyleComposer.js";
import Button from 'primevue/button';
const { t } = useI18n({});

// Refs for focus management
const messageContinueButton = ref(null);
const iframeContinueButton = ref(null);
const messageContainer = ref(null);

const getMessageStyle = computed(() => {
  let top = videoStore.video.design.message_top
  let left = videoStore.video.design.message_left
  let width = videoStore.video.design.message_width
  let height = videoStore.video.design.message_height
  let backgroundColor = videoStore.video.design.message_background_color || videoStore.video.design.question_background_color
  let textColor = videoStore.video.design.message_text_color || videoStore.video.design.prompt_color

  if (clickStore.questionActive) {
    // Custom design question
    if (clickStore.questionActive.question_use_custom_design) {
      backgroundColor = clickStore.questionActive.background_color
      left = clickStore.questionActive.pos_left + '%'
      width = clickStore.questionActive.pos_width + '%'
      height = clickStore.questionActive.pos_height + '%'
      top = clickStore.questionActive.pos_top + '%'
      textColor = clickStore.questionActive.text_color
    } else {
      // Base design question
      backgroundColor = videoStore.video.design.message_background_color || videoStore.video.design.question_background_color
      left = videoStore.video.design.question_left + '%'
      width = videoStore.video.design.question_width + '%'
      height = videoStore.video.design.question_height + '%'
      top = videoStore.video.design.question_top + '%'
      textColor = videoStore.video.design.prompt_color
    }
  }
  // If small device, use full area
  if (playerStore.isMobile) {
    width = 100
    height = 100
    left = 0
    top = 0
  }
  let styles = {
    top: top + '%',
    left: left + '%',
    width: width + '%',
    height: height + '%',
    backgroundColor: backgroundColor,
    color: textColor,
  }
  return styles
})


const hasIframe = computed(() => /<iframe[\s>]/i.test(clickStore.activeMessage || ''))

// Focus management functions
const focusContinueButton = () => {
  nextTick(() => {
    const buttonRef = hasIframe.value ? iframeContinueButton.value : messageContinueButton.value;
    
    if (buttonRef) {
      if (buttonRef.$el) {
        buttonRef.$el.focus();
      } else {
        buttonRef.focus();
      }
      
      // Announce message to screen reader
      announceMessage();
    }
  });
};

// Announce message content to screen reader
const announceMessage = () => {
  const messageText = getPlainTextFromHtml(clickStore.activeMessage);
  const announcement = `Message: ${messageText}. Press Enter or Space to continue.`;
  
  const announcer = document.createElement('div');
  announcer.setAttribute('aria-live', 'assertive');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.style.position = 'absolute';
  announcer.style.left = '-10000px';
  announcer.textContent = announcement;
  document.body.appendChild(announcer);
  
  setTimeout(() => {
    if (document.body.contains(announcer)) {
      document.body.removeChild(announcer);
    }
  }, 1500);
};

// Extract plain text from HTML for screen reader
const getPlainTextFromHtml = (html) => {
  if (!html) return '';
  
  // Create temporary element to extract text
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Remove script and style elements
  const scripts = temp.querySelectorAll('script, style');
  scripts.forEach(el => el.remove());
  
  // Get text content
  return temp.textContent || temp.innerText || '';
};

// Handle keyboard navigation
const handleKeyDown = (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    finishMessage();
  }
};

// Watch for message visibility changes
watch(() => clickStore.activeMessage, (newMessage) => {
  if (newMessage) {
    focusContinueButton();
  }
});

// Focus management on mount
onMounted(() => {
  if (clickStore.activeMessage) {
    focusContinueButton();
  }
  
  // Add keyboard listener for the entire message layer
  document.addEventListener('keydown', handleKeyDown);
});

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
  <div
    id="message-layer"
    ref="messageContainer"
    class="absolute top-0 left-0 w-full h-full overflow-y-scroll no-scrollbar px-3 z-[10]"
    :class="hasIframe ? 'flex flex-col' : clickStore.activeMessage.length < 300 ? 'flex flex-col @sm/player:justify-center items-center py-4' : 'flex flex-col @lg/player:justify-center items-center py-4'"
    :style="getMessageStyle"
    role="dialog"
    aria-modal="true"
    :aria-label="hasIframe ? 'Interactive content message' : 'Video message'"
    :aria-describedby="hasIframe ? 'message-iframe-content' : 'message-text-content'"
  >
    <div v-if="!hasIframe" class="flex flex-col @xs/player:justify-center items-center flex-grow w-[90%] max-w-[800px]">
      <div class="flex flex-col items-center space-y-4 text-center" role="region" aria-live="polite">
        <div 
          id="message-text-content"
          class="lg:text-lg xl:text-xl font-medium" 
          v-html="clickStore.activeMessage" 
          v-once
          role="article"
          tabindex="-1"
        ></div>
        <Button 
          ref="messageContinueButton"
          @click.stop="finishMessage" 
          @keydown.enter.prevent="finishMessage"
          @keydown.space.prevent="finishMessage"
          :style="getBaseButtonStyle()" 
          class="!border-4"
          :aria-label="`Continue button. Message says: ${getPlainTextFromHtml(clickStore.activeMessage)}`"
          role="button"
          tabindex="0"
        >
          {{ localize('continue') }}
        </Button>
      </div>
    </div>

    <div v-else class="w-full h-full relative">
      <div 
        id="message-iframe-content"
        class="w-full h-full" 
        v-safe-html="clickStore.activeMessage" 
        v-once
        role="article"
        aria-label="Embedded interactive content"
      ></div>
      <div class="absolute top-2 right-2 z-20">
        <Button 
          ref="iframeContinueButton"
          @click.stop="finishMessage" 
          @keydown.enter.prevent="finishMessage"
          @keydown.space.prevent="finishMessage"
          :style="getBaseButtonStyle()" 
          class="!border-4"
          aria-label="Continue to next section button"
          role="button"
          tabindex="0"
        >
          {{ localize('continue') }}
        </Button>
      </div>
    </div>
    
    <!-- Screen reader announcement area -->
    <div class="sr-only" aria-live="assertive" aria-atomic="true">
      <span v-if="clickStore.activeMessage">
        Message displayed. Use Tab to navigate to the continue button or press Enter to continue.
      </span>
    </div>
  </div>
</template>

<style>
#message-layer iframe {
  width: 100%;
  height: 100%;
  border: none;
}

#message-layer li {
  list-style-type: none;
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Only show focus outline for keyboard navigation, not mouse clicks */
#message-layer button:focus-visible {
  outline: 2px solid #3B82F6 !important;
  outline-offset: 2px !important;
}

/* Remove default focus outline for mouse users */
#message-layer button:focus:not(:focus-visible) {
  outline: none;
}

/* High contrast mode support for keyboard users */
@media (prefers-contrast: high) {
  #message-layer button:focus-visible {
    outline: 3px solid currentColor !important;
  }
}
</style>